import { z } from "zod";
import { isTimeOfDay, PublicIdSchema, TimeOfDaySchema, TimeOfDayWithSecondsSchema } from "./common";

/**
 * 集荷不可 / 配送不可時刻の 1 区間 (FROM-TO) を**読み出す**ときのスキーマ (#793)。
 *
 * 不可時間は他の時刻と保存経路が違い、`*.unavailable_times` (text 列) へ **JSON 文字列の
 * まま**入る。timestamp 化 (`timeStrToDate`) を通らないので、入口が緩かった時代に入った
 * `25:00` / `09:30:00` のような値がそのまま残っている可能性がある。ここを厳格にすると
 * 受注一覧・拠点一覧が丸ごと 500 になるため、**出力は寛容なまま**にして読めるようにする。
 *
 * 新しい値の検査は入口の {@link UnavailableTimeWindowInput} が担う。
 */
export const UnavailableTimeWindow = z
  .object({
    from: z.string(),
    to: z.string(),
  })
  .superRefine((window, ctx) => {
    if (!window.from || !window.to || window.from <= window.to) return;
    ctx.addIssue({
      code: "custom",
      message: "0時をまたぐ時間帯は設定できません",
      path: ["to"],
    });
  });

/**
 * 時刻の入力欄と違い、不可時間の FROM / TO は**区間を作るための必須項目**なので空を受けない。
 * (`TimeOfDaySchema` は「未入力」として空文字を受理する。空の窓は最適化で黙って捨てられる)
 */
const RequiredTimeOfDay = TimeOfDaySchema.refine((value) => value !== "", {
  message: "時刻を入力してください",
});

/**
 * 集荷不可 / 配送不可時刻の 1 区間を**保存する**ときのスキーマ (#793)。
 *
 * 素の `z.string()` だった頃は `25:00` / `12:75` が JSON 列へそのまま入り、最適化へ渡す
 * `timeStrToSeconds` が null を返して**その窓だけ黙って捨てられていた**。画面には保存した
 * 文字列がそのまま出るため、担当者は「不可時間を設定したのに効いていない」と気づけない。
 * 形式の単一源泉は `@carry/shared` の `HHMM_PATTERN` (`TimeOfDaySchema` 経由)。
 */
export const UnavailableTimeWindowInput = z
  .object({
    from: RequiredTimeOfDay,
    to: RequiredTimeOfDay,
  })
  .superRefine((window, ctx) => {
    // zod v4 は field の検証が落ちても superRefine を続けるので、形式を再確認してから比較する。
    // "9:30" > "10:00" と文字列比較が逆転し、形式エラーに「0時をまたぐ」が重なって
    // どこを直せばよいか読めなくなる (#751 と同じ罠)。
    if (!isTimeOfDay(window.from) || !isTimeOfDay(window.to)) return;
    if (window.from < window.to) return;
    ctx.addIssue({
      code: "custom",
      // 幅ゼロの区間 (12:00〜12:00) も弾く。`toForbiddenWindows` が `end <= start` を捨てるため、
      // 通すと「保存できるのに効かない」窓が作れてしまい #793 の穴が幅ゼロだけ残る。
      // 0 時またぎと同じ文言にすると直し方が読めないのでメッセージを分ける。
      message:
        window.from === window.to
          ? "終了時刻は開始時刻より後に設定してください"
          : "0時をまたぐ時間帯は設定できません",
      path: ["to"],
    });
  });

export const PickupSpot = z.object({
  id: PublicIdSchema,
  spot_name: z.string(),
  address: z.string(),
  latitude: z.string(),
  longitude: z.string(),
  /** 標準集荷時刻 (開始)。注文書で未入力時に注文側へ補完されるデフォルト値。 */
  default_pickup_time_from: z.string().nullable(),
  /** 標準集荷時刻 (終了)。 */
  default_pickup_time_to: z.string().nullable(),
  /** 集荷可能時刻 (開始)。経路最適化の pickup 時間窓フォールバック。 */
  available_time_from: z.string().nullable(),
  /** 集荷可能時刻 (終了)。 */
  available_time_to: z.string().nullable(),
  /** 集荷不可時刻 (複数)。経路最適化で pickup から除外する区間。 */
  unavailable_times: z.array(UnavailableTimeWindow),
  default_pickup_duration_minutes: z.number().int().nullable(),
  remarks: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const ListPickupSpotsInput = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
  /** 拠点名・住所の部分一致 (3 文字以上は FTS5、未満は LIKE) */
  q: z.string().trim().max(200).optional(),
  // false で総件数 COUNT を省略 (コンボボックス等 total 不要の呼出向け)。
  // このとき pagination.total は「取得件数 (続きがあれば limit+1)」を返す。
  // z.coerce.boolean() は "false" を true 扱いするため preprocess で解釈する。
  with_total: z
    .preprocess((v) => (typeof v === "string" ? v === "true" : v), z.boolean())
    .default(true),
});

// 集荷拠点の 4 時刻列は DB の Time 列で、admin は `HH:MM:SS` を送る。
// `TimeOfDaySchema` (HH:MM のみ) を直接使うと既存の更新が一律 400 になるため、
// 秒付きも許容しつつ時・分・秒の範囲を入口で検査する (#805)。
const PickupSpotTimeInput = TimeOfDayWithSecondsSchema.nullable().optional();

export const CreatePickupSpotInput = z.object({
  spot_name: z.string().min(1).max(200),
  address: z.string().min(1).max(500),
  latitude: z.string(),
  longitude: z.string(),
  default_pickup_time_from: PickupSpotTimeInput,
  default_pickup_time_to: PickupSpotTimeInput,
  available_time_from: PickupSpotTimeInput,
  available_time_to: PickupSpotTimeInput,
  unavailable_times: z.array(UnavailableTimeWindowInput).optional(),
  default_pickup_duration_minutes: z.number().int().nullable().optional(),
  remarks: z.string().nullable().optional(),
});

export const UpdatePickupSpotInput = z.object({
  spot_name: z.string().min(1).max(200),
  address: z.string().min(1).max(500),
  latitude: z.string(),
  longitude: z.string(),
  default_pickup_time_from: PickupSpotTimeInput,
  default_pickup_time_to: PickupSpotTimeInput,
  available_time_from: PickupSpotTimeInput,
  available_time_to: PickupSpotTimeInput,
  unavailable_times: z.array(UnavailableTimeWindowInput).optional(),
  default_pickup_duration_minutes: z.number().int().nullable().optional(),
  remarks: z.string().nullable().optional(),
});

export type UnavailableTimeWindowDto = z.infer<typeof UnavailableTimeWindow>;
export type UnavailableTimeWindowInputDto = z.infer<typeof UnavailableTimeWindowInput>;
export type PickupSpotDto = z.infer<typeof PickupSpot>;
export type CreatePickupSpotInputDto = z.infer<typeof CreatePickupSpotInput>;
export type UpdatePickupSpotInputDto = z.infer<typeof UpdatePickupSpotInput>;
